import {ResourceLinkSchema} from '@/resource-link/resourceLink'
import z from 'zod'
import {ApproveActionableSchema} from '@/assignments/ApproveActionable/ApproveActionable.type'
import {BookkeepableSchema} from '@/assignments/BookkeepingActionable/BookkeepingActionable.type'
import {SubAccountCreatableSchema} from '@/assignments/SubAccountCreatable/SubAccountCreatable.type'
import {DimensionSchema} from '@/dimensions/Dimension'
import {ICellRendererParams} from 'ag-grid-community'

export enum AssignmentStatus {
    Pending = 'pending',
    Completed = 'completed',
    Deleted = 'deleted',
    Rejected = 'rejected',
}

export enum AssignmentType {
    BookkeepIncomingEhf = 'BookkeepIncomingEhf',
    BookkeepDocument = 'BookkeepDocument',
    BookkeepOcrBankTransaction = 'BookkeepOcrBankTransaction',
    Approve = 'Approve',
    CreateSupplier = 'CreateSupplier',
    IncomingInvoiceReview = 'IncomingInvoiceReview',
}

export enum ActionableType {
    Bookkeepable = 'Bookkeepable',
    Approve = 'APPROVE',
    SubAccountCreatable = 'SubAccountCreatable'
}

export const CreateAssignmentSchema = z.object({
    title: z.string().nonempty(),
    description: z.string().nonempty().nullish(),
    type: z.string().nonempty(),
    assignee: z.string().nonempty().nullish(),
    resourceLinks: z.array(ResourceLinkSchema),
    status: z.enum(Object.values(AssignmentStatus)).default(AssignmentStatus.Pending),
    dependencies: z.array(z.uuid()).default([]),
    dimensions: z.array(DimensionSchema).default([])
})
export type CreateAssignmentCommand = z.infer<typeof CreateAssignmentSchema>
export type AssignmentFormOutput = z.output<typeof CreateAssignmentSchema>

const ActionableSchema = z.discriminatedUnion('type', [
    ApproveActionableSchema,
    BookkeepableSchema,
    SubAccountCreatableSchema
])
export type Actionable = z.infer<typeof ActionableSchema>

export const AssignmentDependencySchema = CreateAssignmentSchema.pick({
    title: true,
    status: true
}).extend({
    uuid: z.uuid(),
    assignmentNo: z.number(),
})
export type AssignmentDependency = z.infer<typeof AssignmentDependencySchema>

export const AssignmentSchema = CreateAssignmentSchema.omit({dependencies: true}).safeExtend({
    uuid: z.uuid(),
    assignmentNo: z.number(),
    createdAt: z.iso.datetime(),
    deletedAt: z.iso.datetime().nullish(),
    context: ActionableSchema.nullish(),
    result: ResourceLinkSchema.nullish(),
    dependencies: z.array(AssignmentDependencySchema),
})
export type Assignment = z.infer<typeof AssignmentSchema>
export const AssignmentsSchema = z.array(AssignmentSchema)

export const EditAssignmentSchema = AssignmentSchema.pick({
    title: true,
    status: true,
    type: true,
})

export const UpdateAssignmentSchema = AssignmentSchema.omit({
    resourceLinks: true,
    dependencies: true,
    context: true,
}).extend({
    resourceLinks: z.array(ResourceLinkSchema),
    dependencies: z.array(z.uuid()),
    context: z.record(z.string(), z.any()).nullish()
})

export type UpdateAssignmentCommand = z.infer<typeof UpdateAssignmentSchema>

export const FulfillmentSchema = AssignmentSchema.pick({
    title: true,
    description: true,
    assignmentNo: true,
}).extend({
    assignmentUuid: z.string(),
    fulfillOnSuccess: z.boolean()
})
export type Fulfillment = z.infer<typeof FulfillmentSchema>

export type AssignmentCellRenderParams = ICellRendererParams<Assignment> & { context: { openAssignmentDetails: (assignmentUuid: string) => void } }

